import type { BookSummary } from "./book-summary";
import { attachPortraits } from "./attach-portraits";
import { portraitIndexUrl, portraitUrls } from "./portrait-index";
import { compiledChapterSchema, type CompiledChapter } from "./schema/compiled";

export function chapterBundleUrl(bookId: string, chapterId: string): string {
  return `/book-data/${bookId}/${chapterId}.bundle.json`;
}

export async function loadChapterBundle(
  bookId: string,
  chapterId: string,
): Promise<CompiledChapter> {
  const bundle = await fetchBundle(chapterBundleUrl(bookId, chapterId));
  if (bundle) {
    return withCurrentPortraits(bundle, await fetchCharacterAssets(bookId));
  }
  throw new Error(
    `No bundle for ${bookId} ${chapterId}. ` +
      "Run npm run data:compile, then npm run data:stage-public before deploy.",
  );
}

/** Compiled chapters in order, as the book's summary lists them. */
export async function loadBookBundles(bookId: string): Promise<CompiledChapter[]> {
  const summary = await fetch(`/book-data/${bookId}/book.json`)
    .then((response) => (response.ok ? response.json() as Promise<BookSummary> : null))
    .catch(() => null);
  const bundles = await Promise.all((summary?.chapters ?? []).map((chapter) =>
    fetchBundle(chapterBundleUrl(bookId, chapter.id))));
  const chapters = bundles.filter((bundle) => bundle !== null);
  if (chapters.length > 0) {
    const assets = await fetchCharacterAssets(bookId);
    return chapters.map((chapter) => withCurrentPortraits(chapter, assets));
  }
  throw new Error(
    `No bundles for ${bookId}. ` +
      "Run npm run data:compile, then npm run data:stage-public before deploy.",
  );
}

async function fetchBundle(url: string): Promise<CompiledChapter | null> {
  const response = await fetch(url);
  if (!response.ok) {
    return null;
  }
  const contentType = response.headers.get("content-type") ?? "";
  if (!contentType.includes("json")) {
    return null;
  }
  const json: unknown = await response.json();
  return compiledChapterSchema.parse(json);
}

interface CharacterAssets {
  files: string[];
  minorIds?: string[];
  urls?: Record<string, string>;
}

function withCurrentPortraits(
  chapter: CompiledChapter,
  assets: CharacterAssets | null,
): CompiledChapter {
  return assets === null
    ? chapter
    : attachPortraits(chapter, assets.files, assets.minorIds, assets.urls);
}

function strings(value: unknown): string[] | undefined {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : undefined;
}

/** Null when a book has no list, which keeps what its bundles compiled with. */
async function fetchCharacterAssets(bookId: string): Promise<CharacterAssets | null> {
  try {
    const base = import.meta.env.VITE_PORTRAIT_BASE;
    const response = await fetch(portraitIndexUrl(bookId, base));
    if (!response.ok || !(response.headers.get("content-type") ?? "").includes("json")) {
      return null;
    }
    const body = await response.json() as {
      files?: unknown;
      minorIds?: unknown;
      paths?: Record<string, string>;
    };
    const files = strings(body.files);
    return files === undefined ? null : {
      files,
      minorIds: strings(body.minorIds),
      urls: portraitUrls({ files, paths: body.paths }, base),
    };
  } catch {
    return null;
  }
}
